import styled from '@emotion/styled';
import {
  Button,
  SelectMenu,
  Select,
  ComposedInput,
  InputLabel,
  CheckboxFormControl,
  RadioFormControlLabel,
  Menu,
  MenuItem,
  Toast,
  Tabs,
  Tab,
  Switch,
} from '@kep-ui-kit/ui-components';
import { Link } from 'react-router-dom';
import {useFormikContext} from "formik";

export const StyledContainer = styled.div(
  ({
    row,
    centered,
    fitHeight,
    flexWrap,
  }: {
    row?: boolean;
    centered?: boolean;
    fitHeight?: boolean;
    flexWrap?: boolean;
  }) => ({
    position: 'relative',
    width: '100%',
    display: 'flex',
    ...(flexWrap && { flexWrap: 'wrap' }),
    flexDirection: row ? 'row' : 'column',
    justifyContent: centered ? 'center' : 'initial',
    alignItems: centered ? 'center' : 'initial',
    ...(!fitHeight && { height: '100%' }),
  })
);

export const StyledContentContainer = styled(StyledContainer)(
  ({ theme, centered }: { theme?: any; centered?: boolean }) => ({
    padding: `0 ${theme.spacing(3)}`,
    ...(centered && {
      justifyContent: 'center',
      alignItems: 'center',
    }),
    [theme.breakpoints.down('sm')]: {
      padding: `0 ${theme.spacing(2)}`,
    },
  })
);

export const StyledSettingsContainer = styled(StyledContainer)(
  ({ theme, flexWrap }: { theme?: any; flexWrap?: boolean }) => ({
    ...(flexWrap && { flexWrap: 'wrap' }),
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.grey['400']}`,
    borderRadius: '8px',
    '& + &': {
      marginTop: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  })
);

export const StyledLogo = styled.img`
  max-width: 165px;
`;

StyledLogo.defaultProps = {
  src: 'assets/kaltura-logo.svg',
};

export const StyledEmailSentLogo = styled.img`
  max-width: 300px;
  max-height: 300px;
`;

StyledEmailSentLogo.defaultProps = {
  src: 'assets/verify-email.svg',
};

export const StyledCloudRecordingsLogo = styled.img(
  ({ theme }: { theme?: any }) => ({
    maxWidth: '72px',
    maxHeight: '72px',
    marginRight: theme.spacing(3),
  })
);

StyledCloudRecordingsLogo.defaultProps = {
  src: 'assets/cloud-recordings.svg',
};

export const StyledText = styled.p(
  ({
    theme,
    centered,
    fontSize,
    flex,
    bold,
    withDot,
    withMargin,
    smallMargin,
    isSmall,
    withGrey,
    isCursorPointer,
  }: {
    theme?: any;
    centered?: boolean;
    fontSize?: number;
    flex?: boolean;
    bold?: boolean;
    withDot?: boolean;
    withMargin?: boolean;
    smallMargin?: boolean;
    isSmall?: boolean;
    withGrey?: boolean;
    isCursorPointer?: boolean;
  }) => ({
    ...(flex && { display: 'flex' }),
    ...(fontSize
      ? { fontSize }
      : {
          fontSize: theme.typography.pxToRem(isSmall ? 14 : 16),
        }),
    fontWeight: bold ? '700' : '600',
    lineHeight: '20px',
    fontFamily: 'Centra No1',
    margin: 0,
    ...(withMargin && { marginBottom: theme.spacing(smallMargin ? 2 : 3) }),
    ...(centered && {
      textAlign: 'center',
    }),
    ...(isCursorPointer && {
      cursor: 'pointer',
    }),
    ...(withDot && {
      '&::after': {
        content: '"\\2022"',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        lineHeight: '20px',
      },
    }),
    ...(withGrey && {
      span: {
        color: theme.palette.grey['300'],
      },
    }),
  })
);

export const StyledTitle = styled.h1(
  ({
    theme,
    centered,
    isSmall,
    fullWidth,
  }: {
    theme?: any;
    centered?: boolean;
    isSmall?: boolean;
    fullWidth?: boolean;
  }) => ({
    fontSize: theme.typography.pxToRem(isSmall ? 32 : 48),
    fontWeight: '600',
    lineHeight: '62px',
    fontFamily: 'Centra No1',
    margin: 0,
    ...(centered && {
      textAlign: 'center',
    }),
    ...(fullWidth && {
      display: 'block',
      width: '100%',
    }),
  })
);

export const StyledSubTitle = styled(StyledText, {
  shouldForwardProp: (prop) => {
    return prop !== 'isCapitalize' && prop !== 'as';
  },
})(({ theme, isCapitalize }: { theme?: any; isCapitalize?: boolean }) => ({
  fontSize: theme.typography.pxToRem(14),
  marginBottom: theme.spacing(2),
  textTransform: isCapitalize ? 'capitalize' : 'uppercase',
  color: theme.palette.grey['300'],
  fontWeight: 700,
}));

export const StyledBoldText = styled(StyledText)`
  font-weight: 700;
`;

StyledBoldText.defaultProps = {
  as: 'span',
};

export const StyledFormGroup = styled.div(
  ({
    theme,
    isParent,
    row,
    rowSmallMargin,
    bold,
    unbold,
    rowWithLabel,
    withoutMargin,
    smallMargin,
    largeMargin,
    isFormControl,
    withPadding,
    greyLabel,
    maxWidth,
    minWidth,
    fullWidth,
    blackLabel,
    smallLabel,
  }: {
    theme?: any;
    isParent?: boolean;
    row?: boolean;
    rowSmallMargin?: boolean;
    bold?: boolean;
    unbold?: boolean;
    rowWithLabel?: boolean;
    withoutMargin?: boolean;
    smallMargin?: boolean;
    largeMargin?: boolean;
    isFormControl?: boolean;
    withPadding?: boolean;
    greyLabel?: boolean;
    fullWidth?: boolean;
    blackLabel?: boolean;
    smallLabel?: boolean;
    maxWidth?: number | string;
    minWidth?: number | string;
  }) => ({
    maxWidth,
    minWidth,
    display: 'flex',
    flexDirection: row ? 'row' : 'column',
    ...(row && {
      '> div': {
        '&:not(&:last-of-type)': {
          marginRight: theme.spacing(rowSmallMargin ? 3 : 4),
          [theme.breakpoints.down('sm')]: {
            marginRight: theme.spacing(2),
          },
        },
      },
    }),
    ...(!fullWidth && { width: '100%' }),
    ...(!withoutMargin && {
      marginBottom: smallMargin
        ? theme.spacing(2)
        : largeMargin
        ? theme.spacing(5)
        : theme.spacing(3),
    }),
    ...(withPadding && { paddingTop: theme.spacing(2) }),
    ...(!isParent && {
      ...(isFormControl
        ? {
            p: {
              color: blackLabel ? '#000' : theme.palette.grey['100'],
              fontSize: theme.typography.pxToRem(smallLabel ? 14 : 16),
              fontWeight: bold ? '700' : unbold ? '500' : '600',
            },
          }
        : {
            p: {
              fontFamily: 'Centra No1',
              fontSize: theme.typography.pxToRem(14),
              fontWeight: '600',
              lineHeight: theme.typography.pxToRem(16),
              marginTop: theme.spacing(1),
              marginBottom: 0,
              color: theme.palette.danger.main,
            },
            'label:last-of-type': {
              fontSize: theme.typography.pxToRem(smallLabel ? 14 : 16),
              fontWeight: bold ? '700' : unbold ? '500' : '600',
              lineHeight: theme.typography.pxToRem(18),
              fontFamily: 'Centra No1',
              color: blackLabel ? '#000' : theme.palette.grey['100'],
              marginBottom: theme.spacing(1),
              ...(rowWithLabel && { margin: '4px 0 0 8px' }),
              ...(greyLabel && { color: theme.palette.grey[300] }),
              '> span': {
                '&:first-of-type': {
                  fontWeight: 700,
                  marginRight: theme.spacing(1),
                },
                '&:last-of-type': {
                  color: theme.palette.grey[300],
                },
              },
            },
            input: {
              fontFamily: 'Source Sans Pro',
            },
            select: {
              fontFamily: 'Source Sans Pro',
            },
          }),
    }),
    ...(rowWithLabel && {
      flexDirection: 'row',
      alignItems: 'center',
      ...(!withoutMargin && { marginBottom: theme.spacing(1) }),
      '> div': {
        flexDirection: 'row-reverse',
      },
    }),
  })
);

export const StyledInput = styled(ComposedInput, {
  shouldForwardProp: (prop) => {
    return prop !== 'maxWidth';
  },
})(
  ({
    theme,
    maxWidth,
    error,
  }: {
    theme?: any;
    maxWidth?: number;
    error?: boolean;
  }) => ({
    ...(maxWidth && { maxWidth }),
    input: {
      appearance: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'textfield',
      fontSize: theme.typography.pxToRem(16),
    },
    '&.kepuikit-base-input-readOnly': {
      cursor: 'text',
      borderColor: error
        ? theme.palette.danger.main
        : theme.palette.grey['400'],
      input: {
        cursor: 'text !important',
      },
      '&:hover': {
        ...(!error && {
          borderColor: theme.palette.grey['300'],
        }),
      },
    },
  })
);

StyledInput.defaultProps = {
  size: 'medium',
};

export const StyledLabel = styled(InputLabel)(
  ({ theme }: { theme?: any }) => ({})
);

export const StyledErrorLabel = styled.label`
  color: red;
`;

export const StyledSelectMenu = styled(SelectMenu, {
  shouldForwardProp: (prop) => {
    return prop !== 'isSmall';
  },
})(({ theme, isSmall }: { theme?: any; isSmall?: boolean }) => ({
  input: {
    fontSize: theme.typography.pxToRem(16),
  },
  ...(isSmall && {
    maxWidth: '104px',
  }),
}));

StyledSelectMenu.defaultProps = {
  size: 'medium',
};

export const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => {
    return prop !== 'isSmall';
  },
})(({ theme, isSmall }: { theme?: any; isSmall?: boolean }) => ({
  fontWeight: '400',
  letterSpacing: '0.00938em',
  fontFamily: 'Lato,Helvetica Neue,Segoe UI,sans-serif',
  lineHeight: '19px',
  fontSize: theme.typography.pxToRem(16),
  height: '40px',
  ...(isSmall && {
    maxWidth: '104px',
  }),
}));

export const StyledCheckboxFormControl = styled(CheckboxFormControl)(
  ({ theme }: { theme?: any; checked?: boolean }) => ({})
);

export const StyledRadioFormControl = styled(RadioFormControlLabel)(
  ({ theme }: { theme?: any; checked?: boolean }) => ({})
);

export const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => {
    return (
      prop !== 'isAddOn' &&
      prop !== 'withoutMargin' &&
      prop !== 'added' &&
      prop !== 'isBorderless' &&
      prop !== 'withoutDisabledStyles' &&
      prop !== 'isLink' &&
      prop !== 'isSmall'
    );
  },
})(
  ({
    theme,
    isAddOn,
    isBorderless,
    withoutMargin,
    added,
    disabled,
    withoutDisabledStyles,
    isLink,
    isSmall,
  }: {
    theme?: any;
    isAddOn?: boolean;
    isBorderless?: boolean;
    withoutMargin?: boolean;
    added?: boolean;
    disabled?: boolean;
    withoutDisabledStyles?: boolean;
    isLink?: boolean;
    isSmall?: boolean;
  }) => ({
    fontFamily: 'Centra No1',
    textTransform: 'none',
    span: {
      pointerEvents: 'none',
    },
    ...(!withoutMargin && { marginBottom: theme.spacing(3) }),
    ...(isSmall && {
      height: '32px',
      fontSize: theme.typography.pxToRem(14),
    }),
    ...(isBorderless && {
      background: 'none',
      borderColor: added
        ? theme.palette.primary.main
        : disabled
        ? theme.palette.grey['400']
        : theme.palette.grey['100'],
      color: theme.palette.grey['100'],
      '&:hover': {
        background: 'none',
      },
    }),
    ...(isAddOn && {
      color: added ? theme.palette.primary.main : theme.palette.grey['100'],
    }),
    ...(withoutDisabledStyles && {
      '&.Mui-disabled': {
        backgroundColor: theme.palette.primary.main,
        color: '#FFF',
      },
    }),
    ...(isLink && {
      fontFamily: 'Source Sans Pro',
      fontWeight: '600',
      textDecoration: 'none',
      fontSize: theme.typography.pxToRem(16),
      background: 'none',
      border: 'none',
      padding: '0',
      color: theme.palette.primary.main,
      cursor: 'pointer',
      '&:hover': {
        background: 'none',
      },
    }),
  })
);

StyledButton.defaultProps = {
  size: 'large',
};

export const StyledLink = styled(Link, {
  shouldForwardProp: (prop) => {
    return (
      prop !== 'isSmall' &&
      prop !== 'centered' &&
      prop !== 'bold' &&
      prop !== 'disabled'
    );
  },
})(
  ({
    theme,
    isSmall,
    centered,
    disabled,
    bold,
  }: {
    theme?: any;
    isSmall?: boolean;
    centered?: boolean;
    disabled?: boolean;
    bold?: boolean;
  }) => ({
    fontFamily: 'Source Sans Pro',
    fontWeight: bold ? '700' : '600',
    textDecoration: 'none',
    color: theme.palette.primary.main,
    ...(isSmall && { fontSize: theme.typography.pxToRem(14) }),
    ...(centered && {
      display: 'flex',
      width: 'fit-content',
      margin: '0 auto',
    }),
    ...(disabled && {
      color: `${theme.palette.grey['400']} !important`,
      pointerEvents: 'none',
    }),
  })
);

export const StyledTabs = styled(Tabs)(({ theme }: { theme?: any }) => ({
  '.MuiTabs-flexContainer': {
    [theme.breakpoints.down('sm')]: {
      padding: `0 ${theme.spacing(2)}`,
      width: 'max-content',
    },
  },
  '.MuiTabs-indicator': {
    height: '3px',
  },
}));

StyledTabs.defaultProps = {
  indicatorColor: 'primary',
  value: 0,
};

export const StyledTab = styled(Tab)(
  ({
    theme,
    smallMargin,
    isSmall,
  }: {
    theme?: any;
    smallMargin?: boolean;
    isSmall?: boolean;
  }) => ({
    borderBottom: '3px solid transparent',
    paddingLeft: 0,
    paddingRight: 0,
    minWidth: 0,
    fontSize: theme.typography.pxToRem(isSmall ? 14 : 16),
    '&.Mui-selected > *': {
      color: theme.palette.primary.main,
    },
    '&:not(&:last-of-type)': {
      marginRight: theme.spacing(smallMargin ? 1.5 : 3),
    },
  })
);

StyledTab.defaultProps = {
  isSmall: true,
};

export const StyledMenuButton = styled(StyledButton)(
  ({ theme }: { theme?: any }) => ({
    fontSize: theme.typography.pxToRem(16),
    margin: 0,
    paddingLeft: 0,
    paddingRight: 0,
    border: 'none',
    background: 'none',
    color: theme.palette.grey[100],
    '&:hover': {
      background: 'none',
    },
    '&:after': {
      content: '""',
      background: 'no-repeat url("assets/arrow-down.svg") center',
      width: '8px',
      height: '8px',
      marginLeft: theme.spacing(1),
    },
  })
);

export const StyledMenu = styled(Menu, {
  shouldForwardProp: (prop) => {
    return prop !== 'withoutBottomPadding' && prop !== 'withoutTopPadding';
  },
})(
  ({
    theme,
    width,
    withoutTopPadding,
    withoutBottomPadding,
  }: {
    theme?: any;
    width?: number;
    withoutBottomPadding?: boolean;
    withoutTopPadding?: boolean;
  }) => ({
    marginLeft: '-10px',
    '.MuiPaper-root': {
      width,
      maxWidth: '318px',
      overflow: 'hidden',
    },
    '.MuiList-root': {
      paddingTop: withoutTopPadding ? 0 : '12px',
      paddingBottom: withoutBottomPadding ? 0 : '12px',
    },
    li: {
      minHeight: '16px',
      '&:not(:first-of-type)': {
        marginTop: theme.spacing(1),
      },
    },
  })
);

StyledMenu.defaultProps = {
  disableAutoFocusItem: true,
};

export const StyledSwitch = styled(Switch)(
  ({ theme, isSmall }: { theme?: any; isSmall?: boolean }) => ({
    ...(isSmall && {
      width: '32px !important',
      height: '16px !important',
      '.kepuikit-switch-thumb': {
        width: '12px',
        height: '12px',
      },
      '.kepuikit-checked': {
        transform: 'translateX(16px) !important',
      },
    }),
  })
);

export const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => {
    return prop !== 'withoutBottomPadding' && prop !== 'withoutTopPadding';
  },
})(
  ({
    theme,
    withoutTopPadding,
    withoutBottomPadding,
  }: {
    theme?: any;
    withoutBottomPadding?: boolean;
    withoutTopPadding?: boolean;
  }) => ({
    ...(withoutTopPadding && { paddingTop: 0 }),
    ...(withoutBottomPadding && { paddingBottom: 0 }),
    height: '32px',
    '*': {
      pointerEvents: 'none',
    },
  })
);

export const StyledToast = styled(Toast)(({ theme }: { theme?: any }) => ({
  position: 'absolute',
  width: 'fit-content',
  margin: '0 auto',
  top: theme.spacing(4),
  left: 0,
  right: 0,
  zIndex: 1000,
}));

export const StyledDivider = styled.div(({ theme }: { theme?: any }) => ({
  height: '1px',
  width: '100%',
  margin: `${theme.spacing(4)} 0`,
  backgroundColor: theme.palette.grey['500'],
}));

export const StyledSideScreen = styled.div(
  ({ imageUrl }: { imageUrl: string }) => ({
    flex: `0 0 50%`,
    backgroundImage: 'url(' + imageUrl + ')',
    backgroundSize: '100%',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  })
);

export const StyledVideoContainer = styled.div(() => ({
  flex: `0 0 50%`,
  display: 'flex',
}));

export const StyledVideoScreen = styled.video(() => ({
  width: '90%',
}));

interface StyledJsonContainerProps {
  theme?: any;
  maxWidth?: number;
  error?: boolean;
  modalMode?: boolean;
}

export const StyledJsonContainer = styled.textarea<StyledJsonContainerProps>(
  ({ theme, maxWidth, error, modalMode }) => ({
    width: modalMode ? '100%' : '130%', //conditionally set width
    height: modalMode ? 'calc(100% - 60px)' : '200px', //conditionally set height
    padding: '10px',
    fontFamily: 'Centra No1',
    fontSize: theme.typography.pxToRem(16),
    boxSizing: 'border-box',
    border: `1px solid`,
    borderRadius: theme.shape.borderRadius,
    resize: 'vertical',
    whiteSpace: 'pre-wrap',
    borderColor: !!error ? theme.palette.danger.main : theme.palette.grey['400'],
    '&:hover': {
      borderColor: !!error ? theme.palette.danger.main : theme.palette.grey['400']
    },
    ...(error && { outlineColor: theme.palette.danger.main }),
  })
);
